import React, { useState, useEffect } from 'react';
import { FaUniversity, FaEnvelope, FaLock, FaEye, FaEyeSlash, FaUser, FaArrowLeft, FaCheckCircle } from 'react-icons/fa';
import { useNavigate, Link } from 'react-router-dom';
import { useSnackBar } from '../../hooks/useSnackBar';
import { UniversityAuthService } from '../../services/UniversityAuthService';

const UniversitySignup = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    email: '',
    password: '',
    confirmPassword: '',
    university: '',
    position: '',
    department: ''
  });
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isCheckingAuth, setIsCheckingAuth] = useState(true);
  const [errors, setErrors] = useState({});
  const [isEmailSent, setIsEmailSent] = useState(false);
  
  const navigate = useNavigate();
  const enqMsg = useSnackBar();

  // Check if user is already authenticated and redirect to dashboard
  useEffect(() => {
    const checkAuthAndRedirect = () => {
      if (UniversityAuthService.isAuthenticated()) {
        // User is already logged in, redirect to dashboard
        navigate('/university/dashboard', { replace: true });
      } else {
        // User is not authenticated, show signup form
        setIsCheckingAuth(false);
      }
    };

    checkAuthAndRedirect();
  }, [navigate]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
    
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    
    if (!formData.firstName.trim()) {
      newErrors.firstName = 'First name is required';
    }
    
    if (!formData.lastName.trim()) {
      newErrors.lastName = 'Last name is required';
    }
    
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid';
    } else if (!isUniversityEmail(formData.email) && !formData.email.includes('balram.mani@gmail.com')) {
      newErrors.email = 'Please use a university email address (.edu, .ac.uk, etc.)';
    }
    
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters long';
    }
    
    if (!formData.confirmPassword) {
      newErrors.confirmPassword = 'Please confirm your password';
    } else if (formData.password !== formData.confirmPassword) {
      newErrors.confirmPassword = 'Passwords do not match';
    }
    
    if (!formData.university.trim()) {
      newErrors.university = 'University name is required';
    }
    
    if (!formData.position.trim()) {
      newErrors.position = 'Position is required';
    }
    
    if (!formData.department.trim()) {
      newErrors.department = 'Department is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isUniversityEmail = (email) => {
    const universityDomains = [
      // Generic educational TLDs
      '.edu', '.university', '.college', '.school', '.academy',
      '.institute', '.education', '.study', '.degree',

      // UK & Commonwealth
      '.ac.uk', '.edu.au', '.ac.nz', '.ac.za', '.ac.ca',

      // Asia
      '.ac.in', '.edu.in', '.ac.jp', '.ac.kr', '.ac.th',
      '.edu.sg', '.ac.id', '.edu.cn', '.ac.ir', '.ac.hk',
      '.ac.lk', '.ac.bd', '.ac.pk', '.ac.np', '.ac.mm',
      '.edu.ph', '.edu.my', '.ac.vn',

      // Middle East
      '.ac.il', '.ac.eg', '.ac.qa', '.ac.sa', '.ac.ae', '.edu.tr',
      '.edu.jo', '.edu.lb', '.ac.om', '.ac.sy',

      // Africa
      '.ac.ke', '.ac.ug', '.ac.tz', '.ac.ng', '.ac.rw', '.ac.mw',
      '.ac.zm', '.ac.zw', '.ac.na', '.ac.bw', '.ac.gh',

      // Europe (mixed: suffixes + direct .xx domains)
      '.ac.cy', '.ac.be', '.ac.at', '.ac.gr', '.ac.hu',
      '.ac.rs', '.ac.bg', '.ac.me', '.ac.mk', '.ac.cz',
      '.uni-due.de', '.uni-bonn.de', '.uni-heidelberg.de',
      '.tum.de', '.uni-muenchen.de', '.uni-hamburg.de', // Germany
      '.sorbonne-universite.fr', '.paris-saclay.fr', '.polytechnique.edu', // France
      '.unibo.it', '.uniroma1.it', '.polimi.it', '.unipi.it', // Italy
      '.ethz.ch', '.epfl.ch', '.uzh.ch', '.unil.ch', // Switzerland
      '.uio.no', '.ntnu.no', '.uib.no', // Norway
      '.ku.dk', '.dtu.dk', '.au.dk', // Denmark
      '.lu.se', '.uu.se', '.kth.se', '.su.se', // Sweden
      '.uva.nl', '.tudelft.nl', '.rug.nl', '.leidenuniv.nl', // Netherlands
      '.uni.lu', // Luxembourg
      '.uj.edu.pl', '.pw.edu.pl', '.amu.edu.pl', // Poland
      '.cuni.cz', '.muni.cz', // Czech Republic
      '.uniba.sk', '.tuke.sk', // Slovakia
      '.helsinki.fi', '.aalto.fi', '.utu.fi', // Finland
      '.tcd.ie', '.ucd.ie', '.ul.ie', // Ireland
      '.uni.pt', '.ulisboa.pt', '.up.pt', // Portugal
      '.upc.edu', '.uva.es', '.usc.es', // Spain
      '.uni.si', '.uni-lj.si', // Slovenia
      '.unizg.hr', // Croatia
      '.uns.ac.rs', // Serbia
      '.ysu.am', // Armenia
      '.bsu.az', // Azerbaijan
      '.tsu.ge', // Georgia
      '.uni.is', // Iceland
      '.lu.lv', '.rtu.lv', // Latvia
      '.tlu.ee', '.ut.ee', // Estonia
      '.ktu.lt', '.vu.lt', // Lithuania
      '.kpi.ua', '.lnu.edu.ua', '.knu.ua', // Ukraine
      '.bsu.by', // Belarus
      '.msu.ru', '.spbu.ru', '.hse.ru', // Russia

      // Americas
      '.edu.mx', '.edu.br', '.edu.ar', '.edu.pe', '.edu.co',
      '.edu.ve', '.edu.ec', '.edu.uy', '.edu.bo', '.edu.py',
      '.edu.cl', '.edu.do', '.edu.cu', '.edu.sv', '.edu.hn',
      '.edu.gt', '.edu.ni', '.edu.pa',

      // North America
      '.edu', '.edu.tt', '.edu.jm' // USA + Caribbean
    ];
    
    email = email.toLowerCase();
    
    // Extract domain part after "@"
    const atIndex = email.lastIndexOf('@');
    if (atIndex === -1) {
      return false;
    }
    const domainPart = email.substring(atIndex + 1);
    
    // Check if domain ends with any university suffix
    return universityDomains.some(suffix => 
      domainPart.endsWith(suffix)
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!validateForm()) {
      return;
    }
    
    setIsLoading(true);
    
    try {
      const response = await UniversityAuthService.signup(formData);
      
      if (response.success) {
        setIsEmailSent(true);
        enqMsg('Verification email sent! Please check your inbox.', 'success');
      } else {
        enqMsg(response.message || 'Signup failed. Please try again.', 'error');
      }
    } catch (error) {
      enqMsg(error.message || 'An error occurred during signup. Please try again.', 'error');
    } finally {
      setIsLoading(false);
    }
  };

  if (isEmailSent) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#FFE5E3] via-white to-[#F8F9FA] flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-md w-full space-y-8">
          <div className="text-center">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-green-500 to-green-600 rounded-full flex items-center justify-center shadow-lg shadow-green-500/25">
              <FaCheckCircle className="h-8 w-8 text-white" />
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900">
              Check Your Email
            </h2>
            <p className="mt-2 text-sm text-gray-600">
              We've sent a verification link to <strong>{formData.email}</strong>
            </p>
            <p className="mt-4 text-sm text-gray-500">
              Please click the link in the email to verify your account and complete the registration process.
            </p>
          </div>
          
          <div className="bg-white py-8 px-6 shadow-xl rounded-2xl border border-gray-100 text-center">
            <p className="text-sm text-gray-600 mb-4">
              Didn't receive the email? Check your spam folder or
            </p>
            <button
              onClick={() => setIsEmailSent(false)}
              className="text-[#DB4F40] hover:text-[#C64537] font-medium transition-colors duration-200"
            >
              try again
            </button>
          </div>
          
          <div className="text-center">
            <Link
              to="/"
              className="inline-flex items-center text-sm text-gray-500 hover:text-[#DB4F40] transition-colors duration-200"
            >
              <FaArrowLeft className="mr-2" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    );
  }

  // Show loading screen while checking authentication
  if (isCheckingAuth) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-[#DB4F40]/10 via-white to-[#EEA0ED]/10 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
        <div className="max-w-md w-full space-y-8 bg-white/95 backdrop-blur-xl p-10 rounded-3xl shadow-2xl border border-gray-200/50 transform transition-all duration-300 relative z-10">
          <div className="text-center">
            <div className="relative inline-block">
              <div className="mx-auto h-16 w-16 bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-full flex items-center justify-center shadow-lg shadow-[#DB4F40]/25">
                <FaUniversity className="h-8 w-8 text-white" />
              </div>
              <div className="absolute -inset-2 bg-gradient-to-r from-[#DB4F40]/20 to-[#C64537]/20 rounded-full blur-xl opacity-50"></div>
            </div>
            <h2 className="mt-6 text-3xl font-extrabold text-gray-900 bg-gradient-to-r from-[#DB4F40] to-[#C64537] bg-clip-text text-transparent">
              Checking Authentication
            </h2>
            <div className="mt-6 flex justify-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#DB4F40]"></div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#DB4F40]/10 via-white to-[#EEA0ED]/10 flex items-center justify-center py-12 px-4 sm:px-6 lg:px-8 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0 overflow-hidden">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-gradient-to-br from-[#DB4F40]/20 to-[#C64537]/20 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-40 -left-40 w-80 h-80 bg-gradient-to-br from-[#EEA0ED]/20 to-[#DB4F40]/20 rounded-full blur-3xl"></div>
      </div>
      
      <div className="max-w-2xl w-full space-y-8 bg-white/95 backdrop-blur-xl p-10 rounded-3xl shadow-2xl border border-gray-200/50 transform transition-all duration-300 hover:scale-[1.01] relative z-10">
        {/* Header */}
        <div className="text-center">
          <div className="relative inline-block">
            <div className="mx-auto h-16 w-16 bg-gradient-to-r from-[#DB4F40] to-[#C64537] rounded-full flex items-center justify-center shadow-lg shadow-[#DB4F40]/25">
              <FaUniversity className="h-8 w-8 text-white" />
            </div>
            <div className="absolute -inset-2 bg-gradient-to-r from-[#DB4F40]/20 to-[#C64537]/20 rounded-full blur-xl opacity-50"></div>
          </div>
          <h2 className="mt-6 text-3xl font-extrabold text-gray-900 bg-gradient-to-r from-[#DB4F40] to-[#C64537] bg-clip-text text-transparent">
            Recruiter Registration
          </h2>
          <p className="mt-2 text-sm text-gray-600 max-w-md mx-auto">
            Register as a university administrator or professor to upload PhD opportunities and reach qualified candidates
          </p>
        </div>

        {/* Form */}
        <div className="bg-white/80 backdrop-blur-sm py-8 px-6 shadow-xl rounded-2xl border border-gray-100/50 hover:shadow-2xl transition-all duration-300">
          <form className="space-y-6" onSubmit={handleSubmit}>
            {/* Name Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="firstName" className="block text-sm font-medium text-gray-700 mb-2">
                  First Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="firstName"
                    name="firstName"
                    type="text"
                    autoComplete="given-name"
                    value={formData.firstName}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                      errors.firstName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter your first name"
                  />
                </div>
                {errors.firstName && (
                  <p className="mt-1 text-sm text-red-600">{errors.firstName}</p>
                )}
              </div>

              <div>
                <label htmlFor="lastName" className="block text-sm font-medium text-gray-700 mb-2">
                  Last Name
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUser className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="lastName"
                    name="lastName"
                    type="text"
                    autoComplete="family-name"
                    value={formData.lastName}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                      errors.lastName ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Enter your last name"
                  />
                </div>
                {errors.lastName && (
                  <p className="mt-1 text-sm text-red-600">{errors.lastName}</p>
                )}
              </div>
            </div>

            {/* Email Field */}
            <div>
              <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-2">
                University Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <FaEnvelope className="h-5 w-5 text-gray-400" />
                </div>
                <input
                  id="email"
                  name="email"
                  type="email"
                  autoComplete="email"
                  value={formData.email}
                  onChange={handleChange}
                  className={`block w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                    errors.email ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="Enter your university email (.edu, .ac.uk, etc.)"
                />
              </div>
              {errors.email && (
                <p className="mt-1 text-sm text-red-600">{errors.email}</p>
              )}
            </div>

            {/* Password Fields */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-2">
                  Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="password"
                    name="password"
                    type={showPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.password}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-10 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                      errors.password ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Create a password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowPassword(!showPassword)}
                  >
                    {showPassword ? (
                      <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.password && (
                  <p className="mt-1 text-sm text-red-600">{errors.password}</p>
                )}
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-2">
                  Confirm Password
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaLock className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type={showConfirmPassword ? 'text' : 'password'}
                    autoComplete="new-password"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-10 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                      errors.confirmPassword ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="Confirm your password"
                  />
                  <button
                    type="button"
                    className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  >
                    {showConfirmPassword ? (
                      <FaEyeSlash className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    ) : (
                      <FaEye className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                    )}
                  </button>
                </div>
                {errors.confirmPassword && (
                  <p className="mt-1 text-sm text-red-600">{errors.confirmPassword}</p>
                )}
              </div>
            </div>

            {/* University Information */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <div>
                <label htmlFor="university" className="block text-sm font-medium text-gray-700 mb-2">
                  University
                </label>
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <FaUniversity className="h-5 w-5 text-gray-400" />
                  </div>
                  <input
                    id="university"
                    name="university"
                    type="text"
                    value={formData.university}
                    onChange={handleChange}
                    className={`block w-full pl-10 pr-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                      errors.university ? 'border-red-300 bg-red-50' : 'border-gray-300'
                    }`}
                    placeholder="University name"
                  />
                </div>
                {errors.university && (
                  <p className="mt-1 text-sm text-red-600">{errors.university}</p>
                )}
              </div>

              <div>
                <label htmlFor="position" className="block text-sm font-medium text-gray-700 mb-2">
                  Position
                </label>
                <input
                  id="position"
                  name="position"
                  type="text"
                  value={formData.position}
                  onChange={handleChange}
                  className={`block w-full px-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                    errors.position ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Professor, Admin"
                />
                {errors.position && (
                  <p className="mt-1 text-sm text-red-600">{errors.position}</p>
                )}
              </div>

              <div>
                <label htmlFor="department" className="block text-sm font-medium text-gray-700 mb-2">
                  Department
                </label>
                <input
                  id="department"
                  name="department"
                  type="text"
                  value={formData.department}
                  onChange={handleChange}
                  className={`block w-full px-3 py-3 border rounded-xl focus:outline-none focus:ring-2 focus:ring-[#DB4F40] focus:border-transparent transition-all duration-200 ${
                    errors.department ? 'border-red-300 bg-red-50' : 'border-gray-300'
                  }`}
                  placeholder="e.g., Computer Science"
                />
                {errors.department && (
                  <p className="mt-1 text-sm text-red-600">{errors.department}</p>
                )}
              </div>
            </div>

            {/* Submit Button */}
            <div>
              <button
                type="submit"
                disabled={isLoading}
                className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-xl text-white bg-gradient-to-r from-[#DB4F40] to-[#C64537] hover:from-[#C64537] hover:to-[#B73E31] focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-[#DB4F40] disabled:opacity-50 disabled:cursor-not-allowed transition-all duration-200 hover:shadow-lg hover:shadow-[#DB4F40]/25"
              >
                {isLoading ? (
                  <div className="flex items-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2" />
                    Creating account...
                  </div>
                ) : (
                  'Create Account'
                )}
              </button>
            </div>

            {/* Links */}
            <div className="text-center space-y-3">
              <p className="text-sm text-gray-600">
                Already have an account?{' '}
                <Link
                  to="/university/login"
                  className="font-medium text-[#DB4F40] hover:text-[#C64537] transition-colors duration-200"
                >
                  Sign in here
                </Link>
              </p>
              
              {/* Contact Admin Text */}
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3">
                <p className="text-xs text-gray-500 text-center">
                  Having trouble? Please contact{' '}
                  <a 
                    href="mailto:admin@phdscanner.com" 
                    className="font-medium text-[#DB4F40] hover:text-[#C64537] transition-colors duration-200"
                  >
                    admin@phdscanner.com
                  </a>
                  {' '}if you face any issues
                </p>
              </div>
            </div>
          </form>
        </div>

        {/* Back to Home */}
        <div className="text-center">
          <Link
            to="/"
            className="inline-flex items-center text-sm text-gray-500 hover:text-[#DB4F40] transition-colors duration-200"
          >
            <FaArrowLeft className="mr-2" />
            Back to Home
          </Link>
        </div>
      </div>
    </div>
  );
};

export default UniversitySignup;
